import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, type Variants } from 'framer-motion';
import { Search, MessageCircle, Terminal, KeyRound, X, ArrowUpRight, Download, Sparkles } from 'lucide-react';
import contentData from '../data/website_content.json';
import { useRouter } from '../context/RouterContext';
import { CURRENT_VERSION, DOWNLOAD_FILENAME } from '../config/version';
import { DownloadModal } from './DownloadModal';

interface NavbarProps {
  onOpenSearch: () => void;
}

// ─── Cinematic Mobile Menu Bar Wipe Overlay ──────────────────────────────────
const MobileMenuOverlay: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  navLinks: { label: string; path: string; desc: string }[];
  navigate: (path: string) => void;
  contact: { 
    whatsappUrl: string; 
    whatsappDisplay: string;
    secondaryWhatsappUrl?: string;
    secondaryWhatsappDisplay?: string;
  };
  onOpenDownload: () => void;
}> = ({ isOpen, onClose, navLinks, navigate, contact, onOpenDownload }) => {
  const handleNav = (path: string) => {
    navigate(path);
    onClose();
  };

  const SPRING_EASE = [0.16, 1, 0.3, 1] as [number, number, number, number];
  const EXIT_EASE = [0.7, 0, 0.84, 0] as [number, number, number, number];

  const barVariants: Variants = {
    closed: { scaleY: 0, originY: 0 },
    open: (i: number) => ({
      scaleY: 1,
      originY: 0,
      transition: { duration: 0.55, ease: SPRING_EASE, delay: i * 0.04 },
    }),
    exit: (i: number) => ({
      scaleY: 0,
      originY: 1,
      transition: { duration: 0.40, ease: EXIT_EASE, delay: (4 - i) * 0.035 },
    }),
  };

  const linkVariants: Variants = {
    closed: { opacity: 0, y: 24 },
    open: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: SPRING_EASE, delay: 0.22 + i * 0.06 },
    }),
    exit: { opacity: 0, y: -12, transition: { duration: 0.2 } },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[200] overflow-hidden lg:hidden" role="dialog" aria-modal="true" aria-label="Navigation Menu">
          {/* 5 staggered bar wipe panels */}
          {[0, 1, 2, 3, 4].map(i => (
            <motion.div
              key={i}
              custom={i}
              variants={barVariants}
              initial="closed"
              animate="open"
              exit="exit"
              style={{
                position: 'absolute',
                top: `${i * 20}%`,
                left: 0,
                right: 0,
                height: '21%',
                background: '#0A080A',
                zIndex: 0,
              }}
            />
          ))}

          {/* Content layer */}
          <div className="relative z-10 flex flex-col h-full px-6 sm:px-10 py-8 overflow-y-auto">
            {/* Top bar */}
            <div className="flex items-center justify-between mb-8">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, transition: { delay: 0.3 } }}
                exit={{ opacity: 0 }}
                className="flex items-center gap-2"
              >
                <span className="text-xs font-mono text-muted tracking-widest uppercase font-bold">
                  LASHARY FB MARKET SUITE
                </span>
                <span className="px-2 py-0.5 rounded bg-rose/15 text-rose text-[10px] font-mono font-bold border border-rose/25">
                  v{CURRENT_VERSION}
                </span>
              </motion.div>
              <motion.button
                initial={{ opacity: 0, rotate: -90 }}
                animate={{ opacity: 1, rotate: 0, transition: { delay: 0.3, ease: [0.16, 1, 0.3, 1] } }}
                exit={{ opacity: 0 }}
                onClick={onClose}
                className="w-10 h-10 rounded-full border border-white/[0.12] flex items-center justify-center text-soft-white hover:text-white hover:border-rose/40 transition-all focus-visible:outline-none"
                aria-label="Close menu"
              >
                <X className="w-5 h-5" />
              </motion.button>
            </div>

            {/* Nav links */}
            <nav className="flex-1 flex flex-col justify-center gap-1 my-4">
              {navLinks.map((link, i) => (
                <motion.button
                  key={link.label}
                  custom={i}
                  variants={linkVariants}
                  initial="closed"
                  animate="open"
                  exit="exit"
                  onClick={() => handleNav(link.path)}
                  className="group flex items-end justify-between py-3.5 border-b border-white/[0.06] hover:border-rose/30 text-left transition-colors w-full focus-visible:outline-none cursor-pointer"
                >
                  <div className="flex items-baseline gap-4">
                    <span className="text-xs font-mono text-muted/60 w-5 shrink-0">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className="text-2xl sm:text-3xl font-black tracking-tight text-soft-white group-hover:text-rose transition-colors leading-none">
                      {link.label}
                    </span>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-muted/60 group-hover:text-rose group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all shrink-0" />
                </motion.button>
              ))}
            </nav>

            {/* Bottom CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0, transition: { delay: 0.55, ease: [0.16, 1, 0.3, 1] } }}
              exit={{ opacity: 0 }}
              className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-auto pt-6 border-t border-white/[0.08]"
            >
              <button
                onClick={() => {
                  onClose();
                  onOpenDownload();
                }}
                className="btn-download-action inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-bold cursor-pointer"
              >
                <Download className="w-4 h-4 stroke-[2.5]" />
                <span>DOWNLOAD v{CURRENT_VERSION}</span>
              </button>

              <a
                href={contact.secondaryWhatsappUrl || contact.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onClose}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-jade/15 text-jade border border-jade/30 text-xs font-semibold hover:bg-jade/25 transition-all"
              >
                <MessageCircle className="w-4 h-4" />
                <span>Chat with Aswad Lashari: {contact.secondaryWhatsappDisplay || "+92 371 043 4552"}</span>
              </a>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

// ─── Main Unified Navbar ─────────────────────────────────────────────────────
export const Navbar: React.FC<NavbarProps> = ({ onOpenSearch }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const { brand, contact } = contentData;
  const { currentPath, navigate } = useRouter();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll only when mobile menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const navLinks = [
    { label: 'Tools',       path: '/tools',       desc: 'Facebook Marketplace software & free utilities' },
    { label: 'Features',    path: '/features',    desc: 'Flagship capabilities & workspaces' },
    { label: 'Comparisons', path: '/comparisons', desc: 'Desktop vs Cloud bots & extensions' },
    { label: 'Changelog',   path: '/changelog',   desc: 'Release history & milestones' },
    { label: 'Blog',        path: '/blog',        desc: 'Operator guides & playbooks' },
    { label: 'Pricing',     path: '/pricing',     desc: 'PKR 1,600 · $15 / month' },
    { label: 'Support',     path: '/support',     desc: 'Direct founder assistance' },
    { label: 'Download',    path: '/download',    desc: `1-Click direct installer v${CURRENT_VERSION}` },
  ];

  const desktopLinks = [
    { label: 'Tools',       path: '/tools' },
    { label: 'Features',    path: '/features' },
    { label: 'Comparisons', path: '/comparisons' },
    { label: 'Changelog',   path: '/changelog' },
    { label: 'Blog',        path: '/blog' },
    { label: 'Pricing',     path: '/pricing' },
    { label: 'Support',     path: '/support' },
  ];

  return (
    <>
      {/* Complete Production Runtime Download Pop-up Modal */}
      <DownloadModal
        isOpen={isDownloadModalOpen}
        onClose={() => setIsDownloadModalOpen(false)}
      />

      {/* Cinematic Mobile Menu Overlay (Strictly lg:hidden) */}
      <MobileMenuOverlay
        isOpen={menuOpen}
        onClose={() => setMenuOpen(false)}
        navLinks={navLinks}
        navigate={navigate}
        contact={contact}
        onOpenDownload={() => setIsDownloadModalOpen(true)}
      />

      {/* Fixed Sticky Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${
          scrolled
            ? 'py-2.5 bg-obsidian/92 backdrop-blur-2xl border-b border-white/[0.08] shadow-[0_4px_30px_rgba(10,8,10,0.8)]'
            : 'py-3.5 bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-4">

            {/* ── Brand Logo & Title ── */}
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-3 group text-left focus-visible:outline-none shrink-0 cursor-pointer"
            >
              <div className="relative w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-action p-[1.5px] shadow-sm transition-transform group-hover:scale-105 group-hover:shadow-glow-rose shrink-0">
                <div className="w-full h-full bg-charcoal rounded-[10px] flex items-center justify-center overflow-hidden">
                  {!logoError ? (
                    <picture>
                      <source srcSet="/brand/app_icon_32.webp 1x, /brand/app_icon_64.webp 2x" type="image/webp" />
                      <img
                        src="/brand/app_icon_32.webp"
                        alt="Lashary Logo"
                        width="32"
                        height="32"
                        className="w-7 h-7 sm:w-8 sm:h-8 object-contain"
                        onError={() => setLogoError(true)}
                        loading="eager"
                        decoding="async"
                      />
                    </picture>
                  ) : (
                    <Terminal className="w-5 h-5 text-rose" />
                  )}
                </div>
              </div>
              <div className="flex flex-col">
                <span className="font-black text-sm sm:text-base tracking-tight text-ivory flex items-center gap-1.5 leading-none">
                  LASHARY
                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-rose/15 text-rose border border-rose/30 font-bold">
                    SUITE
                  </span>
                </span>
                <span className="text-[11px] text-muted font-medium tracking-wide mt-0.5">
                  FB Market Suite
                </span>
              </div>
            </button>

            {/* ── DESKTOP ONLY Horizontal Navigation Bar ── */}
            <nav className="hidden lg:flex items-center gap-1 bg-charcoal/90 border border-white/[0.10] rounded-full px-3 py-1.5 backdrop-blur-md shadow-sm">
              {desktopLinks.map(link => {
                const isActive = currentPath === link.path;
                return (
                  <button
                    key={link.label}
                    onClick={() => navigate(link.path)}
                    className={`relative text-xs font-semibold px-3.5 py-1.5 rounded-full transition-all duration-200 focus-visible:outline-none cursor-pointer ${
                      isActive
                        ? 'text-white font-bold'
                        : 'text-muted hover:text-soft-white hover:-translate-y-0.5'
                    }`}
                  >
                    {isActive && (
                      <motion.span
                        layoutId="nav-pill"
                        className="absolute inset-0 bg-rose/20 border border-rose/35 rounded-full"
                        transition={{ type: 'spring', bounce: 0.2, duration: 0.35 }}
                      />
                    )}
                    <span className="relative z-10">{link.label}</span>
                  </button>
                );
              })}
            </nav>

            {/* ── Controls (Desktop & Mobile) ── */}
            <div className="flex items-center gap-2 sm:gap-2.5">

              {/* 
                INTERACTIVE TOP BAR VERSION BADGE 
                Clicking opens the Complete Production Runtime Download Pop-up Modal!
              */}
              <button
                onClick={() => setIsDownloadModalOpen(true)}
                title="Click to view v1.5 Complete Production Runtime Specs & Download"
                className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-xl bg-plum-black/80 hover:bg-plum/60 border border-rose/30 hover:border-rose/50 text-[11px] font-mono text-soft-white transition-all cursor-pointer group shadow-sm hover:shadow-glow-rose"
              >
                <span className="w-2 h-2 rounded-full bg-jade animate-pulse group-hover:scale-125 transition-transform" />
                <span className="font-bold text-rose group-hover:text-white">v{CURRENT_VERSION}</span>
                <span className="text-muted text-[10px] hidden sm:inline">WIN64</span>
              </button>

              {/* Search Modal Trigger (Ctrl+K) */}
              <button
                onClick={onOpenSearch}
                className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-xl bg-charcoal/90 border border-white/[0.08] text-muted hover:text-soft-white transition-all text-xs font-mono focus-visible:outline-none cursor-pointer hover:border-rose/30"
                aria-label="Search (Ctrl+K)"
              >
                <Search className="w-3.5 h-3.5 text-muted group-hover:text-rose" />
                <span>Search</span>
                <kbd className="text-[9px] px-1.5 py-0.5 rounded bg-white/[0.06] text-muted border border-white/[0.08]">
                  ⌘K
                </kbd>
              </button>

              {/* DESKTOP ONLY: "DOWNLOAD v1.5" CTA Button (Rose → Vermilion) */}
              <button
                onClick={() => setIsDownloadModalOpen(true)}
                className="btn-download-action hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold cursor-pointer font-sans"
              >
                <Download className="w-3.5 h-3.5 stroke-[2.5]" />
                <span>DOWNLOAD v{CURRENT_VERSION}</span>
              </button>

              {/* Mobile: Search Icon Only */}
              <button
                onClick={onOpenSearch}
                className="md:hidden p-2 rounded-lg bg-charcoal/90 border border-white/[0.08] text-soft-white hover:text-white focus-visible:outline-none cursor-pointer"
                aria-label="Search"
              >
                <Search className="w-4 h-4 text-rose" />
              </button>

              {/* 
                MOBILE ONLY: Hamburger Menu Button (lg:hidden) 
                Never shows on Desktop PC!
              */}
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="lg:hidden p-2 rounded-lg bg-white/[0.05] border border-white/[0.08] text-slate-300 hover:text-white transition-all focus-visible:outline-none cursor-pointer"
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={menuOpen}
              >
                <div className="w-5 h-4 flex flex-col justify-between">
                  <motion.span
                    animate={{ rotate: menuOpen ? 45 : 0, y: menuOpen ? 7 : 0 }}
                    transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                    className="block h-px bg-current rounded-full origin-center"
                  />
                  <motion.span
                    animate={{ opacity: menuOpen ? 0 : 1, scaleX: menuOpen ? 0 : 1 }}
                    transition={{ duration: 0.2 }}
                    className="block h-px bg-current rounded-full"
                  />
                  <motion.span
                    animate={{ rotate: menuOpen ? -45 : 0, y: menuOpen ? -9 : 0 }}
                    transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                    className="block h-px bg-current rounded-full origin-center"
                  />
                </div>
              </button>

            </div>

          </div>
        </div>
      </header>
    </>
  );
};

export default Navbar;
